import React, { useMemo } from 'react';
import { Check, TriangleAlert } from 'lucide-react';
import type { FunnelCounters } from '../../lib/funnel';
import { alertFlowChecks, type AlertFlowCounts } from '../../lib/alertFlow';
import AlertFlowLedger from './AlertFlowLedger';
import AlertFlowPath from './AlertFlowPath';

interface AlertFlowSankeyProps {
  counters: FunnelCounters;
  onOpenCampaigns?: (campId?: string) => void;
}

/** The backend's counters, per plane (candidates, verdicts, threats, campaigns, actions), as the flow reads them. */
export function funnelToSankeyCounts(c: FunnelCounters): AlertFlowCounts {
  const unique = c.candidates.unique;
  const duplicated = c.candidates.duplicated;
  const threats = c.verdicts.threat;
  const falsePositives = c.verdicts.suppressed;
  const escalated = c.verdicts.escalated;
  return {
    raw: c.candidates.registered,
    analyzed: unique + duplicated,
    unique,
    anomalies: threats + falsePositives + escalated,
    baseline: c.verdicts.baseline,
    threats,
    falsePositives,
    duplicated,
    pending: c.candidates.pending,
    analyzing: c.candidates.analyzing,
    errors: c.candidates.error,
    campaigns: c.campaigns.open,
    inCampaign: c.threats.inCampaign,
    remediations: c.actions.total,
    remPending: c.actions.pending,
    remConfirmed: c.actions.confirmed,
    remInProgress: c.actions.inProgress,
    remCompleted: c.actions.completed,
    remCancelled: c.actions.cancelled,
    remReverted: c.actions.reverted,
    escalated,
    standalone: c.threats.standalone,
  };
}

/**
 * The Overview's Alert Flow tab (docs/c3/DESIGN.md, Alert Flow): one check that every stage adds up, the flow drawn as
 * its main path through the seven pipeline stages, and, folded away, the counts per plane the backend sends, for whoever
 * needs to audit them.
 */
const AlertFlowSankey: React.FC<AlertFlowSankeyProps> = ({ counters, onOpenCampaigns }) => {
  const counts = useMemo(() => funnelToSankeyCounts(counters), [counters]);
  const checks = alertFlowChecks(counts);
  const broken = checks.filter((check) => !check.ok);
  const detail = checks.map((check) => `${check.stage}: ${check.parts.toLocaleString('en-US')} of ${check.input.toLocaleString('en-US')}`).join(' · ');

  return (
    <div className="af">
      <div className="af-head">
        <h2>
          Alert Flow <span className="meta">every candidate accounted for, stage by stage</span>
        </h2>
        <span className={`af-check${broken.length ? ' is-bad' : ''}`} title={detail} role="status">
          {broken.length ? (
            <TriangleAlert size={14} strokeWidth={1.75} aria-hidden />
          ) : (
            <Check size={14} strokeWidth={1.75} aria-hidden />
          )}
          {broken.length
            ? `Does not add up at ${broken.map((check) => check.stage).join(', ')}`
            : 'Every count adds up'}
        </span>
      </div>
      <AlertFlowPath counts={counts} onOpenCampaigns={onOpenCampaigns ? () => onOpenCampaigns() : undefined} />
      <details className="af-ledger">
        <summary>Show the counts behind it</summary>
        <p className="af-ledger-note">
          The backend counts each kind of record on its own: candidates, verdicts, threats, campaigns and remediations.
          The flow above re-arranges these same figures by pipeline stage; each line below must add up.
        </p>
        <AlertFlowLedger counters={counters} />
      </details>
    </div>
  );
};

export default AlertFlowSankey;
